/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState, useEffect, useRef, ChangeEvent } from 'react';
import { Tooltip as CTooltip } from 'lib/components/Tooltip';
import { Modal } from 'lib/components/Modal';
import api from 'api';
import { getFirebaseContext } from 'utils/firebase';
import { OPEN_INVITE_STATUSES } from 'lib/users';
import { userHasPermission } from 'utils/permissions';
import { Permissions } from 'lib/permissions/roles';
import { useRolesForOrg } from 'hooks/useRolesForOrg';
import { logAndCaptureException, logAndCaptureMessage } from 'utils';
import { ColumnService } from 'lib/services/directory';
import { EResponseTypes } from 'lib/types/api';
import ToastActions from 'redux/toast';
import { useAppDispatch } from 'redux/hooks';
import {
  ESnapshotExists,
  EOrganization,
  EUser,
  EUnsubscribe,
  ERef,
  EInvite,
  ERequestTypes
} from 'lib/types';
import { RoleType } from 'lib/enums';
import { getModelFromSnapshot } from 'lib/model';
import { UserModel } from 'lib/model/objects/userModel';
import { ArrowDownIcon, DeleteIcon, Mail } from '../../icons/index';
import Firebase from '../../EnoticeFirebase';

const getInitials = (firstName: string, lastName: string) => {
  if (!firstName[0] || !lastName[0]) return 'N/A';
  return firstName[0].toUpperCase() + lastName[0].toUpperCase();
};

const makeAvatar = (firstName: string, lastName: string) => (
  <svg
    width="35"
    height="36"
    viewBox="0 0 35 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.5 36C27.165 36 35 27.9411 35 18C35 8.05888 27.165 0 17.5 0C7.83502 0 0 8.05888 0 18C0 27.9411 7.83502 36 17.5 36Z"
      fill="#FEC504"
    ></path>
    <text
      x={!firstName || !lastName ? '5' : '8'}
      y="23"
      className="font-bold text-sm"
      fill="white"
    >
      {getInitials(firstName, lastName)}
    </text>
  </svg>
);

type Member = { data: EUser | EInvite; ref: ERef<EUser> | ERef<EInvite> };
type MemberWithStatus = {
  data: EUser | EInvite;
  ref: ERef<EUser> | ERef<EInvite>;
  status: string;
};

type DeleteMember = {
  status: string;
  ref: ERef<EUser> | ERef<EInvite>;
  email: string;
};

type OrgSettingsMemberTableProps = {
  activeOrganization: ESnapshotExists<EOrganization>;
  refresh: boolean;
  setRefresh: (refresh: boolean) => void;
  user: ESnapshotExists<EUser>;
  setToastMessage: ({
    message,
    invitedSuccessfully
  }: {
    message: string;
    invitedSuccessfully: boolean;
  }) => void;
};

const usePrevious = <T extends unknown>(value: T): T | undefined => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

function OrgSettingsMemberTable(props: OrgSettingsMemberTableProps) {
  const dispatch = useAppDispatch();

  const { activeOrganization, user, setToastMessage } = props;
  const canModifyMembers = userHasPermission(
    user,
    Permissions.ORGANIZATION_MODIFY_MEMBERS
  );

  const prevProps = usePrevious({
    activeOrganization,
    setToastMessage
  });
  const [invitees, setInvitees] = useState<Member[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [showResendDialog, setShowResendDialog] = useState(false);
  const [resendInvitationValue, setResendInvitationValue] = useState<{
    email: string;
    role: number | undefined;
    isExistingUser: boolean;
  }>();
  const [showDeleteMemberDialog, setShowDeleteMemberDialog] = useState(false);
  const [deleteMemberValue, setDeleteMemberValue] = useState<DeleteMember>();

  const ctx = getFirebaseContext();

  const getInvitees = (): EUnsubscribe => {
    return ctx
      .invitesRef()
      .where('organizationId', '==', activeOrganization.id)
      .where('status', 'in', OPEN_INVITE_STATUSES)
      .onSnapshot(result => {
        const invitees = result.docs
          ? result.docs.map(doc => {
              return {
                data: doc.data(),
                ref: doc.ref
              };
            })
          : [];
        setInvitees(invitees);
      });
  };

  const orgInvitedMembers = (
    organization: ESnapshotExists<EOrganization>
  ): EUnsubscribe => {
    return ctx
      .usersRef()
      .where('allowedOrganizations', 'array-contains', organization.ref)
      .onSnapshot(result => {
        const members = result.docs
          .filter(user => {
            const userModel = getModelFromSnapshot(UserModel, ctx, user);
            const [roleError, role] = userModel.getRoleForOrganization(
              organization.id
            );

            if (roleError) {
              logAndCaptureMessage(
                'User missing role for member organization',
                {
                  organizationId: organization.id,
                  userId: userModel.id
                }
              );
              return false;
            }

            return !!role?.canInvite;
          })
          .filter(user => !user.data().anonymous)
          .map(doc => ({
            data: doc.data(),
            ref: doc.ref
          }));
        setMembers(members);
      });
  };

  const getMembers = (): EUnsubscribe => {
    const unsubs: EUnsubscribe[] = [];

    const invitedUnsub = orgInvitedMembers(activeOrganization);
    unsubs.push(invitedUnsub);

    return () => {
      unsubs.map(unsub => unsub());
    };
  };

  useEffect(() => {
    const inviteesUnsub = getInvitees();
    const membersUnsub = getMembers();

    return () => {
      inviteesUnsub();
      membersUnsub();
    };
  }, []);

  const onUpdatePrevProps = async () => {
    if (activeOrganization !== prevProps?.activeOrganization) {
      await Promise.all([getMembers(), getInvitees()]);
    }
  };

  useEffect(() => {
    void onUpdatePrevProps();
  }, [props]);

  const { inviteRoles } = useRolesForOrg(activeOrganization);

  const transformInvitees = (invitees: Member[]) => {
    if (!invitees.length) return [];
    return invitees.map(invitee =>
      Object.assign(invitee, { status: 'Invited' })
    );
  };

  const transformMembers = (members: Member[]) => {
    if (!members.length) return [];

    // sort current user to top of the table
    members.sort((_, b) =>
      b.ref.id === Firebase.auth().currentUser?.uid ? 1 : -1
    );

    return members.map(member => Object.assign(member, { status: 'Active' }));
  };

  const rowsFromState = () => {
    const inviteeRows = transformInvitees(invitees);
    const memberRows = transformMembers(members);
    return memberRows.concat(
      inviteeRows.filter(
        (inv: MemberWithStatus) =>
          !memberRows
            .map((m: MemberWithStatus) => m.data.email)
            .includes(inv.data.email)
      ) as MemberWithStatus[]
    );
  };

  const handleRoleSelect = async (
    e: ChangeEvent<HTMLSelectElement>,
    ref: ERef<any>
  ) => {
    const role = Number(e.target.value);
    const roleLabel = RoleType.by_value(role)?.label;
    const confirm = window.confirm(
      `Are you sure you would like to change this user's role to ${roleLabel}?`
    );

    if (!confirm) return false;

    const user = await ref.get();

    const { error: updateRoleError } = await api.safePost('users/update-role', {
      userId: user.id,
      organizationId: activeOrganization.id,
      role
    });
    if (updateRoleError) {
      dispatch(
        ToastActions.toastSuccess({
          headerText: 'Failed to update role',
          bodyText: 'Please wait a few minutes and try again.'
        })
      );
      return false;
    }
    dispatch(
      ToastActions.toastSuccess({
        headerText: 'Role updated',
        bodyText: `${user.data().email} now has the ${roleLabel} role for ${
          activeOrganization.data().name
        }`
      })
    );

    await getMembers();

    return true;
  };

  const deleteInvitee = async (inviteId: string) => {
    const { error: deleteInviteError } = await api.safePost(
      'users/delete-invited-member',
      { inviteId }
    );
    if (deleteInviteError) {
      logAndCaptureException(
        ColumnService.AUTH_AND_USER_MANAGEMENT,
        deleteInviteError,
        'Failed to delete invite'
      );
      setToastMessage({
        message: 'Failed to delete invite',
        invitedSuccessfully: false
      });
      setShowDeleteMemberDialog(false);
      return false;
    }
    return true;
  };

  const deleteMember = async (ref: ERef<any>) => {
    const req: ERequestTypes['users/delete-active-member'] = {
      userId: ref.id,
      organizationId: activeOrganization.id
    };
    const {
      error: deleteActiveMemberError
    }: EResponseTypes['users/delete-active-member'] = await api.safePost(
      'users/delete-active-member',
      req
    );

    if (deleteActiveMemberError) {
      logAndCaptureException(
        ColumnService.AUTH_AND_USER_MANAGEMENT,
        deleteActiveMemberError,
        'Failed to delete member'
      );
      setToastMessage({
        message: 'Failed to delete member',
        invitedSuccessfully: false
      });
      setShowDeleteMemberDialog(false);
    }
  };

  const deleteRow = async ({ status, ref }: DeleteMember) => {
    try {
      if (status === 'Invited') {
        const deleted = await deleteInvitee(ref.id);
        if (!deleted) {
          return;
        }
      }

      if (status === 'Active') {
        await deleteMember(ref);
      }

      setShowDeleteMemberDialog(false);
      setToastMessage({ message: 'User removed.', invitedSuccessfully: true });
    } catch (err) {
      console.error(err);
    }
  };

  const resendInvite = async () => {
    try {
      if (!resendInvitationValue) {
        throw new Error('No invitation value to resend');
      }

      if (!resendInvitationValue.email) {
        throw new Error('No email for resend invite');
      }

      if (!resendInvitationValue.role) {
        throw new Error('No role for resend invite');
      }

      const resendValue = {
        email: resendInvitationValue.email,
        role: resendInvitationValue.role,
        resend: true
      };
      const { response: result, error: inviteError } = await api.safePost(
        'users/invite',
        {
          inviteData: [resendValue],
          organizationId: activeOrganization.id,
          isExistingUserInvite: resendInvitationValue.isExistingUser
        }
      );

      if (inviteError) {
        throw inviteError;
      }
      const { invited, resend } = result || {};

      if (invited?.length && resend) {
        setToastMessage({ message: 'Email sent!', invitedSuccessfully: true });
        setShowResendDialog(false);
      }
    } catch (err) {
      logAndCaptureException(
        ColumnService.WEB_PLACEMENT,
        err,
        'Failed to resend invite'
      );
      setToastMessage({
        message: 'Failed to resend invite',
        invitedSuccessfully: false
      });
      setShowResendDialog(false);
    }
  };

  const rows = rowsFromState() as MemberWithStatus[];
  return (
    <>
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            <th className="bg-column-gray-50 text-left text-xs leading-4 font-medium text-column-gray-500 uppercase tracking-wider"></th>
            <th className="pl-2 pr-6 py-3 bg-column-gray-50 text-left text-xs leading-4 font-medium text-column-gray-500 uppercase tracking-wider">
              NAME
            </th>
            <th className="px-6 py-3 bg-column-gray-50 text-left text-xs leading-4 font-medium text-column-gray-500 uppercase tracking-wider">
              EMAIL
            </th>
            <th className="px-6 py-3 bg-column-gray-50 text-left text-xs leading-4 font-medium text-column-gray-500 uppercase tracking-wider">
              STATUS
            </th>
            <th className="px-6 py-3 bg-column-gray-50 text-left text-xs leading-4 font-medium text-column-gray-500 uppercase tracking-wider">
              ROLE
            </th>
            <th className="px-6 py-3 bg-column-gray-50 text-left text-xs leading-4 font-medium text-column-gray-500 uppercase tracking-wider">
              ACTIONS
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 rounded-b-lg">
          {rows
            .slice(0, 19000)
            .map(
              ({
                data,
                status,
                ref
              }: {
                data: EUser | EInvite;
                status: string;
                ref: ERef<EUser> | ERef<EInvite>;
              }) => {
                const disableRoleChangeReason = !canModifyMembers
                  ? 'You are not allowed to change roles'
                  : status === 'Invited'
                  ? 'You have to wait until this user accepts the invitation'
                  : Firebase.auth().currentUser?.uid === ref.id
                  ? 'You cannot change your own role'
                  : '';
                return (
                  <tr key={ref.id}>
                    <td className="pl-6 pr-2 py-4 whitespace-no-wrap text-sm leading-5 text-column-gray-900">
                      {makeAvatar(
                        (data as EUser).firstName || '',
                        (data as EUser).lastName || ''
                      )}
                    </td>
                    <td className="pl-2 pr-6 py-4 whitespace-no-wrap text-sm leading-5 text-column-gray-900">
                      {(data as EUser).firstName || ''}{' '}
                      {(data as EUser).lastName || ''}
                    </td>
                    <td className="px-6 py-4 whitespace-no-wrap text-sm leading-5 text-column-gray-900">
                      {data.email}
                    </td>
                    <td className="px-6 py-4 whitespace-no-wrap text-sm leading-5 text-column-gray-900">
                      <div className="flex">
                        {status === 'Active' && (
                          <svg
                            width="16"
                            height="17"
                            viewBox="0 0 16 17"
                            fill="none"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M0 8.5C0 3.80558 3.58172 0 8 0C12.4163 0.00501605 15.9953 3.80768 16 8.5C16 13.1944 12.4183 17 8 17C3.58172 17 0 13.1944 0 8.5ZM6.68957 11.9651L12.4039 5.8937C12.6178 5.65841 12.6178 5.2854 12.4039 5.05011C12.1846 4.80895 11.8229 4.80226 11.5959 5.03521L6.28557 10.6774L4.40384 8.67807C4.39922 8.67298 4.39453 8.66804 4.38978 8.66313C4.16277 8.43018 3.80103 8.43687 3.58179 8.67807C3.36255 8.91926 3.36884 9.30362 3.59585 9.53656L5.88158 11.9651C6.1047 12.2022 6.46641 12.2022 6.68957 11.9651Z"
                              fill="#2D9BDC"
                            />
                          </svg>
                        )}
                        {status === 'Invited' && (
                          <svg
                            width="17"
                            height="18"
                            viewBox="0 0 17 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M8 1L8.00053 0.5H8V1ZM16 9H16.5V8.99947L16 9ZM12.404 6.54686L12.7576 6.90047L12.7637 6.89422L12.404 6.54686ZM6.68971 12.2612L7.04321 12.6148L7.04326 12.6147L6.68971 12.2612ZM12.404 5.7529L12.7637 5.40554L12.7636 5.40552L12.404 5.7529ZM11.596 5.73887L11.2486 5.37916L11.2425 5.38531L11.596 5.73887ZM6.28571 11.0492L5.93216 11.4027L6.28571 11.7563L6.63927 11.4027L6.28571 11.0492ZM4.40398 9.16744L4.04415 9.51473L4.05043 9.521L4.40398 9.16744ZM4.38992 9.15338L4.7384 8.79483L4.73726 8.79373L4.38992 9.15338ZM3.58192 9.16744L3.22227 8.8201L3.22227 8.8201L3.58192 9.16744ZM3.59599 9.97544L3.94959 9.62183L3.94333 9.61578L3.59599 9.97544ZM5.88172 12.2612L5.52816 12.6147L5.52819 12.6147L5.88172 12.2612ZM8 0.5C3.30558 0.5 -0.5 4.30558 -0.5 9H0.5C0.5 4.85787 3.85787 1.5 8 1.5V0.5ZM16.5 8.99947C16.495 4.30744 12.6926 0.505016 8.00053 0.5L7.99947 1.5C12.1401 1.50443 15.4956 4.85996 15.5 9.00053L16.5 8.99947ZM8 17.5C12.6944 17.5 16.5 13.6944 16.5 9H15.5C15.5 13.1421 12.1421 16.5 8 16.5V17.5ZM-0.5 9C-0.5 13.6944 3.30558 17.5 8 17.5V16.5C3.85787 16.5 0.5 13.1421 0.5 9H-0.5ZM12.0505 6.19331L6.33615 11.9076L7.04326 12.6147L12.7576 6.90041L12.0505 6.19331ZM12.0444 6.10025C12.0711 6.12794 12.0711 6.17182 12.0444 6.1995L12.7637 6.89422C13.1647 6.479 13.1647 5.82075 12.7637 5.40554L12.0444 6.10025ZM11.9434 6.09852C11.9718 6.07111 12.017 6.0719 12.0444 6.10027L12.7636 5.40552C12.3526 4.97995 11.6743 4.96815 11.2487 5.37922L11.9434 6.09852ZM6.63927 11.4027L11.9496 6.09242L11.2425 5.38531L5.93216 10.6956L6.63927 11.4027ZM4.05043 9.521L5.93216 11.4027L6.63927 10.6956L4.75753 8.81389L4.05043 9.521ZM4.04143 9.51193C4.04372 9.51415 4.0444 9.51487 4.04421 9.51467L4.76375 8.82022C4.75431 8.81044 4.74562 8.80185 4.7384 8.79483L4.04143 9.51193ZM3.94158 9.51479C3.96898 9.48641 4.01419 9.48563 4.04257 9.51303L4.73726 8.79373C4.31163 8.38265 3.63335 8.39446 3.22227 8.8201L3.94158 9.51479ZM3.94333 9.61578C3.91496 9.58838 3.91417 9.54317 3.94158 9.51479L3.22227 8.8201C2.8112 9.24574 2.823 9.92401 3.24864 10.3351L3.94333 9.61578ZM6.23527 11.9076L3.94954 9.62188L3.24243 10.329L5.52816 12.6147L6.23527 11.9076ZM6.33621 11.9076C6.30828 11.9355 6.2631 11.9354 6.23524 11.9076L5.52819 12.6147C5.94659 13.0331 6.62481 13.033 7.04321 12.6148L6.33621 11.9076Z"
                              fill="#2D9BDC"
                            />
                          </svg>
                        )}
                        <div className="ml-2">{status}</div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-no-wrap text-sm leading-5 text-column-gray-900">
                      <div className="relative" title={disableRoleChangeReason}>
                        <select
                          value={
                            (data as EUser).roles?.[activeOrganization.id] ||
                            (data as EInvite).role
                          }
                          onChange={e => handleRoleSelect(e, ref)}
                          disabled={!!disableRoleChangeReason}
                          className="appearance-none w-full bg-white hover:border-column-gray-500 py-2 rounded leading-tight focus:outline-none focus:shadow-outline"
                        >
                          {inviteRoles.map(role => {
                            return (
                              <option key={role.value} value={role.value}>
                                {role.label}
                              </option>
                            );
                          })}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-column-gray-700">
                          <ArrowDownIcon className="h-4 w-4" />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-6 whitespace-no-wrap text-sm font-medium text-column-gray-900 flex items-center">
                      {Firebase.auth().currentUser?.uid !== ref.id &&
                        canModifyMembers && (
                          <div
                            onClick={() => {
                              const { email } = data;
                              setShowDeleteMemberDialog(true);
                              setDeleteMemberValue({ status, ref, email });
                            }}
                            className="cursor-pointer mr-3 inline-block"
                          >
                            <CTooltip
                              helpText={'Remove user'}
                              position={'bottom'}
                            >
                              <DeleteIcon alt="trash" />
                            </CTooltip>
                          </div>
                        )}
                      {status === 'Invited' && canModifyMembers && (
                        <div
                          onClick={() => {
                            setShowResendDialog(true);
                            setResendInvitationValue({
                              email: data.email,
                              role: data.role,
                              isExistingUser: !!(data as EInvite)?.user
                            });
                          }}
                          className="cursor-pointer inline-block"
                        >
                          <CTooltip
                            helpText={'Resend invite'}
                            position={'bottom'}
                          >
                            <Mail alt="resend" />
                          </CTooltip>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              }
            )}
        </tbody>
      </table>
      {showResendDialog && resendInvitationValue && (
        <Modal
          id="resend-invitation-modal"
          title="Resend email?"
          onClose={() => setShowResendDialog(false)}
          primaryAction={{
            buttonText: 'Confirm',
            onClick: () => {
              void resendInvite();
              setShowResendDialog(false);
            },
            type: 'button'
          }}
        >
          <p>{`An email invite will be resent to ${resendInvitationValue.email}`}</p>
        </Modal>
      )}
      {showDeleteMemberDialog && (
        <Modal
          id="delete-member-modal"
          title="Remove this user from your organization"
          onClose={() => setShowDeleteMemberDialog(false)}
          primaryAction={{
            buttonText: 'Remove User',
            onClick: () => deleteMemberValue && deleteRow(deleteMemberValue),
            type: 'button',
            destructive: true
          }}
        >
          <p>Are you sure you would like to remove this user?</p>
        </Modal>
      )}
    </>
  );
}

export default OrgSettingsMemberTable;
